/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-12
 */

// Add third-party dependencies.
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  PersonAdd,
  Business,
  CheckCircle,
  Login,
  Edit,
  Mail,
  GroupAdd,
  Info,
  Notifications,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import type { DashboardOverviewResponse } from '../../../../types/dashboard';
import { formatStudioActivity } from '../../../../utils/dashboardHelpers';

interface RecentActivityCardProps {
  dashboardData?: DashboardOverviewResponse;
}

/**
 * Recent activity card component for the dashboard.
 * Displays recent activity feed from backend API data.
 *
 * @example
 * return (
 *   <RecentActivityCard dashboardData={dashboardData} />
 * );
 *
 * @returns {React.ReactElement} The recent activity card component
 */
const RecentActivityCard: React.FC<RecentActivityCardProps> = ({
  dashboardData,
}) => {
  const { t } = useTranslation('dashboard');

  // Get activities from API data or use empty array
  const apiActivities = dashboardData?.data?.recent_activity || [];

  // Helper function to get icon based on activity type
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'login':
        return <Login color="primary" />;
      case 'account_created':
        return <PersonAdd color="success" />;
      case 'email_verified':
        return <CheckCircle color="success" />;
      case 'studio_joined':
        return <Business color="secondary" />;
      case 'profile_update':
        return <Edit color="info" />;
      case 'invitation_sent':
        return <Mail color="warning" />;
      case 'invitation_accepted':
        return <GroupAdd color="success" />;
      default:
        return <Info color="primary" />;
    }
  };

  // Transform API activities using helper function
  const activities = apiActivities.map((activity, index) => {
    const formattedActivity = formatStudioActivity(activity);
    return {
      id: `activity-${index}`,
      type: activity.type,
      icon: getActivityIcon(activity.type),
      description: activity.description,
      time: formattedActivity.formattedTime,
    };
  });

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Notifications sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6" component="h2">
            {t('recentActivity.title')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('recentActivity.description')}
        </Typography>

        {activities.length > 0 ? (
          <List sx={{ p: 0 }}>
            {activities.map((activity, index) => (
              <ListItem
                key={activity.id || `activity-${index}`}
                sx={{
                  px: 0,
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 0,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {activity.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2">
                      {activity.description}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="span"
                    >
                      {activity.time}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              textAlign: 'center',
            }}
          >
            <Notifications
              sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }}
            />
            <Typography variant="body1" color="text.secondary">
              {t('recentActivity.noActivity')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('recentActivity.noActivityDescription')}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('recentActivity.comingSoon')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RecentActivityCard;
